import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import {
  Button,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { useWindowSize } from "@/hooks"

const DEFAULT_DISPLAY_RANGE = 6

export interface PageNavProps {
  className?: string
  page: number
  totalPages: number
  onPageChange?: (page: number) => void
}

const PageNav = ({
  className,
  page,
  totalPages,
  onPageChange,
}: PageNavProps) => {
  const { isMobile } = useWindowSize()
  const [pageElement, setPageElement] = useState<number[]>([])
  const [pageRange, setPageRange] = useState<number[]>([1, 1]) // [start, end]

  useEffect(() => {
    let displayRange = DEFAULT_DISPLAY_RANGE
    if (isMobile) {
      displayRange = 3
    }
    const start = Math.max(1, page - Math.floor((displayRange - 1) / 2))
    const end = Math.min(start + (displayRange - 1), totalPages)

    const elements: number[] = []
    for (let i = start; i <= end; i++) {
      elements.push(i)
    }
    setPageElement(elements)
    setPageRange([start, end])
  }, [page, totalPages, isMobile])

  const handelPagePrev = () => {
    if (page > 1) {
      onPageChange?.(page - 1)
    }
  }

  const handelPageNext = () => {
    if (page < totalPages) {
      onPageChange?.(page + 1)
    }
  }

  return (
    <Pagination className={cn(className, "w-fit")}>
      <PaginationContent>
        <PaginationItem>
          <Button variant="ghost" onClick={handelPagePrev}>
            <ChevronLeft />
          </Button>
        </PaginationItem>
        {pageRange[0] > 1 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        {pageElement.map((elem) => (
          <PaginationItem key={`page-${elem}`} className="h-10">
            <Button
              variant={elem == page ? "outline" : "ghost"}
              size="icon"
              onClick={() => onPageChange?.(elem)}
            >
              <span className="text-sm">{elem}</span>
            </Button>
          </PaginationItem>
        ))}
        {pageRange[1] < totalPages && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        <PaginationItem>
          <Button variant="ghost" onClick={handelPageNext}>
            <ChevronRight />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

export default PageNav
